/**
 * Calf Lifecycle Service
 * CRUD operations for CalfLifecycleEvents collection
 * Tracks calf phase transitions (birth-arrival → colostrum → milk-phase → etc.)
 *
 * @collection Users/{userId}/CalfLifecycleEvents
 */

import { db } from "../../../firebase";
import {
	DEFAULT_PRODUCTION_TYPE,
	getLifecycleStageOrder,
} from "../../../constants/productionTypes";

// ============================================================================
// WRITE OPERATIONS
// ============================================================================

/**
 * Add a lifecycle transition event
 * @param {string} userId
 * @param {string} mac - Animal MAC
 * @param {Object} data - { stage, previousStage, transitionDate, notes }
 * @returns {Promise<string>} Document ID
 */
export async function addLifecycleEvent(userId, mac, data) {
	const timestamp = Date.now();
	const docId = timestamp.toString();

	const cleanData = Object.fromEntries(
		Object.entries(data).filter(([, v]) => v !== undefined)
	);

	const record = {
		...cleanData,
		MAC: mac,
		Uid: userId,
		t: timestamp,
		Archived: false,
	};

	await db
		.collection("Users")
		.doc(userId)
		.collection("CalfLifecycleEvents")
		.doc(docId)
		.set(record);

	return docId;
}

/**
 * Update an existing lifecycle event
 * @param {string} userId
 * @param {string} docId
 * @param {Object} updates
 */
export async function updateLifecycleEvent(userId, docId, updates) {
	const cleanUpdates = Object.fromEntries(
		Object.entries(updates).filter(([, v]) => v !== undefined)
	);

	await db
		.collection("Users")
		.doc(userId)
		.collection("CalfLifecycleEvents")
		.doc(docId)
		.update({ ...cleanUpdates, updatedAt: Date.now() });
}

/**
 * Archive a lifecycle event
 */
export async function archiveLifecycleEvent(userId, docId) {
	await updateLifecycleEvent(userId, docId, { Archived: true, ArchivedAt: Date.now() });
}

// ============================================================================
// READ OPERATIONS
// ============================================================================

/**
 * Get all lifecycle events for an animal
 * @param {string} userId
 * @param {string} mac
 * @returns {Promise<Array>}
 */
export async function getLifecycleEvents(userId, mac) {
	const snapshot = await db
		.collection("Users")
		.doc(userId)
		.collection("CalfLifecycleEvents")
		.where("MAC", "==", mac)
		.where("Archived", "==", false)
		.orderBy("t", "desc")
		.get();

	return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
}

/**
 * Get all lifecycle events for a user (all animals)
 * @param {string} userId
 * @returns {Promise<Array>}
 */
export async function getAllLifecycleEvents(userId) {
	const snapshot = await db
		.collection("Users")
		.doc(userId)
		.collection("CalfLifecycleEvents")
		.where("Archived", "==", false)
		.orderBy("t", "desc")
		.get();

	return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
}

/**
 * Get the current lifecycle stage for an animal
 * (most recent non-archived event)
 * @param {string} userId
 * @param {string} mac
 * @returns {Promise<string|null>} Current stage or null
 */
export async function getCurrentLifecycleStage(userId, mac) {
	const events = await getLifecycleEvents(userId, mac);
	if (!events.length) return null;
	return events[0].stage || null;
}

/**
 * Validate a stage transition
 * @param {string} fromStage
 * @param {string} toStage
 * @returns {{ valid: boolean, message?: string }}
 */
// ============================================================================
// ANALYTICS / COMPUTATION
// ============================================================================

/**
 * Calculate the duration spent in each lifecycle stage.
 * Expects events sorted by transitionDate descending (newest first — the
 * order returned by getLifecycleEvents).
 *
 * @param {Array} events - lifecycle events sorted desc by transitionDate/t
 * @returns {Array<{ stage: string, startDate: number, endDate: number|null, durationDays: number }>}
 */
export function calculateStageDurations(events) {
	if (!events || events.length === 0) return [];

	// Work oldest-first so each event's start is known
	const sorted = [...events].reverse();
	const now = Date.now();

	return sorted.map((event, idx) => {
		const startDate = event.transitionDate || event.t;
		const startMs = typeof startDate === "number" ? startDate : new Date(startDate).getTime();

		let endMs = null;
		if (idx < sorted.length - 1) {
			const nextDate = sorted[idx + 1].transitionDate || sorted[idx + 1].t;
			endMs = typeof nextDate === "number" ? nextDate : new Date(nextDate).getTime();
		}

		const durationMs = (endMs || now) - startMs;
		const durationDays = Math.max(0, Math.floor(durationMs / (1000 * 60 * 60 * 24)));

		return {
			stage: event.stage,
			startDate: startMs,
			endDate: endMs,
			durationDays,
		};
	});
}

/**
 * Calculate age in days from a birth date to now.
 * @param {number|string|Date} birthDate
 * @returns {number|null} age in whole days, or null if birthDate is falsy
 */
export function getAgeInDays(birthDate) {
	if (!birthDate) return null;
	const birthMs = typeof birthDate === "number" ? birthDate : new Date(birthDate).getTime();
	if (isNaN(birthMs)) return null;
	return Math.max(0, Math.floor((Date.now() - birthMs) / (1000 * 60 * 60 * 24)));
}

/**
 * Validate a stage transition for a given production type
 * @param {string} fromStage
 * @param {string} toStage
 * @param {string} [productionType="calf"] - Determines the valid stage set/order
 * @returns {{ valid: boolean, message?: string }}
 */
export function validateStageTransition(fromStage, toStage, productionType = DEFAULT_PRODUCTION_TYPE) {
	const stageOrder = getLifecycleStageOrder(productionType);

	if (!toStage || !stageOrder.includes(toStage)) {
		return { valid: false, message: `Invalid target stage: ${toStage}` };
	}

	// Allow any forward transition or same-stage re-entry
	if (!fromStage) return { valid: true };

	const fromIdx = stageOrder.indexOf(fromStage);
	const toIdx = stageOrder.indexOf(toStage);

	// Allow forward transitions; backward transitions emit a warning but are valid
	if (toIdx < fromIdx) {
		return { valid: true, message: `Warning: backward transition from ${fromStage} to ${toStage}` };
	}

	return { valid: true };
}
